import { useCallback, useMemo } from 'react';
import {
  getElementQuantityProperties,
  getSelectedElementQuantityProperty,
} from '@shared/helpers/element-quantity.helpers';
import {
  FactoryQuantityRecord,
  elementListSelectableQuantityNames,
  IElementQuantityProperty,
  IFactoryQuantityProperty,
} from '@shared/models/element-quantities.model';
import { IElement } from '@shared/models/project.model';
import { sortInOrderOf } from '@shared/helpers/array.helpers';
import { ElementCategoryID } from '@shared/models/element-categories.model';
import { useApplyProjectChanges } from '@client/store/project/use-apply-project-changes.hook';
import { quantityChangesToProjectChanges } from '@shared/helpers/pipeline/property-change-to-project-change.helpers';
import {
  getElementById,
  getBuilding,
  isElement,
} from '@shared/helpers/recursive-element.helpers';

/**
 * Get all quantity properties for an element as an array.
 * @param element
 * @returns
 */
export const useElementQuantityProperties = (
  element: IElement,
): IElementQuantityProperty[] =>
  useMemo(
    () => getElementQuantityProperties(element.quantity),
    [element.quantity],
  );

/**
 * Get the selected quantity property for an element.
 * Will automatically fallback if undefined.
 * @param element
 * @returns
 */
export const useSelectedQuantityProperty = (element: IElement) =>
  useMemo(() => getSelectedElementQuantityProperty(element), [element]);

/**
 * Get an update function for the quantity properties for an element.
 * Converts quantity changes to {@link ProjectChange} items and delegates
 * to {@link useApplyProjectChanges} so changes are recorded in proposal mode.
 */
export const useUpdateQuantity = (): ((
  element: IElement,
  changes: FactoryQuantityRecord | IFactoryQuantityProperty[],
) => Promise<IElement>) => {
  const applyChanges = useApplyProjectChanges();

  return useCallback(
    async (
      element: IElement,
      changes: FactoryQuantityRecord | IFactoryQuantityProperty[],
    ): Promise<IElement> => {
      const projectChanges = quantityChangesToProjectChanges(element, changes);

      if (projectChanges.length === 0) {
        return element;
      }

      const applyResult = await applyChanges(projectChanges);

      if (applyResult.error ?? applyResult.cancelled) {
        return element;
      }

      const updatedElement = getElementById(
        getBuilding(applyResult.project),
        element.id,
      );

      if (!isElement(updatedElement)) {
        throw new Error('Updated Element not found');
      }

      return updatedElement;
    },
    [applyChanges],
  );
};

const selectableQuantityNameSet = new Set<string>(
  elementListSelectableQuantityNames,
);

/**
 * Quantities the element-list unit menu can switch between.
 * Includes hidden rows such as pieces.
 */
export const useIconQuantityProperties = (element: IElement) => {
  const quantityProps = useElementQuantityProperties(element);
  return useMemo(
    () =>
      quantityProps
        .filter(
          ({ name }) =>
            element.category_id !== ElementCategoryID.Labour &&
            selectableQuantityNameSet.has(name),
        )
        .sort((a, b) =>
          sortInOrderOf(elementListSelectableQuantityNames, a, b, 'name'),
        ),
    [element.category_id, quantityProps],
  );
};
